import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowDownRight, ArrowUpRight, FileDown, Scale } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { downloadPdf } from "@/lib/download";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(n || 0);
const MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const MONTH_NAMES = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const now = new Date(), thisYear = now.getFullYear();
const pct = (part, total) => (total ? `%${((part / total) * 100).toLocaleString("tr-TR", { maximumFractionDigits: 1 })}` : "—");

function Totals({ items, testid }) {
  return (
    <div className="fin-totals" data-testid={testid}>
      {items.map(([label, value, Icon, cls], i) => (
        <div className="metric" key={label} data-testid={`${testid}-${i}`}>
          <div className="metric-top"><span>{label}</span><Icon size={18} /></div>
          <strong className={cls}>{money(value)}</strong>
        </div>
      ))}
    </div>
  );
}

function MonthChart({ months, onSelect }) {
  const data = months.map((m, i) => ({ ...m, label: MONTHS[i] }));
  return (
    <section className="panel" data-testid="report-monthly-chart">
      <div className="panel-head"><div><h3>Aylık gelir / gider</h3><p>Bir aya tıklayarak detayını açın</p></div></div>
      <div className="fin-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} barGap={2} onClick={(e) => e?.activeTooltipIndex != null && onSelect(Number(e.activeTooltipIndex) + 1)}>
            <CartesianGrid vertical={false} stroke="#e8eef5" />
            <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#94a3b8" }} />
            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => new Intl.NumberFormat("tr-TR", { notation: "compact", maximumFractionDigits: 1 }).format(v)} />
            <Tooltip formatter={(v) => money(v)} cursor={{ fill: "#f4f7fb" }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="income" name="Gelir" fill="#0f9f6e" radius={[4, 4, 0, 0]} style={{ cursor: "pointer" }} />
            <Bar dataKey="expense" name="Gider" fill="#f59e0b" radius={[4, 4, 0, 0]} style={{ cursor: "pointer" }} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

function MonthTable({ months, totals, month, onSelect }) {
  return (
    <section className="panel" data-testid="report-month-table">
      <div className="panel-head"><div><h3>Aylık özet</h3><p>Ay satırına tıklayınca detay açılır</p></div></div>
      <div className="fin-row head"><span>AY</span><span>GELİR</span><span>GİDER</span><span>NET</span></div>
      {months.map((m, i) => (
        <button key={m.month} className={month === m.month ? "fin-row active" : "fin-row"} onClick={() => onSelect(m.month)} data-testid={`report-month-row-${m.month}`}>
          <b>{MONTH_NAMES[i]}</b><span className="income-text">{money(m.income)}</span><span className="expense-text">{money(m.expense)}</span>
          <strong className={m.net < 0 ? "negative" : ""}>{money(m.net)}</strong>
        </button>
      ))}
      <div className="fin-row total" data-testid="report-year-total-row"><b>Toplam</b><span className="income-text">{money(totals.income)}</span><span className="expense-text">{money(totals.expense)}</span><strong className={totals.net < 0 ? "negative" : ""}>{money(totals.net)}</strong></div>
    </section>
  );
}

function CategoryList({ title, items, total, kind }) {
  return (
    <div className="fin-cats" data-testid={`report-categories-${kind}`}>
      <h4>{title}</h4>
      {items.length === 0 && <p className="empty">Bu ay kayıt yok.</p>}
      {items.map((c) => (
        <div className="fin-cat" key={c.category}>
          <div><b>{c.category}</b><span>{money(c.amount)} · {pct(c.amount, total)}</span></div>
          <div className="compare-bar"><i style={{ width: `${(c.amount / total) * 100}%`, background: kind === "income" ? "#0f9f6e" : "#f59e0b" }} /></div>
        </div>
      ))}
    </div>
  );
}

function TxList({ rows }) {
  if (!rows.length) return <p className="empty" data-testid="report-transactions-empty">Bu ay kayıtlı hareket yok.</p>;
  return (
    <div className="fin-txs" data-testid="report-transactions">
      {rows.map((t, i) => (
        <div className="fin-tx" key={t.id} data-testid={`report-transaction-${i}`}>
          <span className={t.type === "income" ? "tx-icon income" : "tx-icon expense"}>{t.type === "income" ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}</span>
          <div><b>{t.title}</b><small>{t.category} · {t.date.split("-").reverse().join(".")}</small></div>
          <strong className={t.type === "income" ? "income-text" : "expense-text"}>{t.type === "income" ? "+" : "−"}{money(t.amount)}</strong>
        </div>
      ))}
    </div>
  );
}

function MonthDetail({ year, month, d }) {
  return (
    <section className="panel fin-detail" data-testid="report-month-detail">
      <div className="panel-head"><div><span className="eyebrow">AY DETAYI</span><h3 data-testid="report-detail-title">{MONTH_NAMES[month - 1]} {year}</h3><p>Kâr marjı: <b data-testid="report-detail-margin">{pct(d.net, d.income)}</b></p></div></div>
      <Totals testid="report-detail-totals" items={[["Gelir", d.income, ArrowUpRight, "income-text"], ["Gider", d.expense, ArrowDownRight, "expense-text"], ["Net kâr", d.net, Scale, d.net < 0 ? "negative" : ""]]} />
      <div className="fin-cat-grid">
        <CategoryList title="Gelir kategorileri" kind="income" items={d.categories.income} total={d.income} />
        <CategoryList title="Gider kategorileri" kind="expense" items={d.categories.expense} total={d.expense} />
      </div>
      <h4 className="fin-subtitle">Hareketler</h4>
      <TxList rows={d.transactions} />
    </section>
  );
}

export default function Reports() {
  const [year, setYear] = useState(thisYear), [month, setMonth] = useState(now.getMonth() + 1), [data, setData] = useState(null), [detail, setDetail] = useState(null), [chart, setChart] = useState("donut");
  useEffect(() => { axios.get(`${API}/reports/finance`, { params: { year } }).then((r) => setData(r.data)).catch(() => toast.error("Rapor yüklenemedi, sayfayı yenileyin")); }, [year]);
  useEffect(() => { setDetail(null); axios.get(`${API}/reports/finance/month`, { params: { year, month } }).then((r) => setDetail(r.data)).catch(() => {}); }, [year, month]);
  const download = () => downloadPdf("/reports/finance/month.pdf", `gelir-gider-${year}-${String(month).padStart(2, "0")}.pdf`, { year, month, chart });
  return (
    <>
      <div className="page-title">
        <div><span className="eyebrow">ANALİZ</span><h1 data-testid="page-title">Finansal Raporlar</h1><p>Gelir & Gider kayıtlarınızdan aylık özet. Bugüne kadar gerçekleşen hareketler sayılır.</p></div>
        <div className="title-actions">
          <button className="outline-btn" onClick={() => downloadPdf("/reports/finance/year.pdf", `gelir-gider-${year}.pdf`, { year, chart })} data-testid="report-year-download-button"><FileDown size={16} />Yıllık rapor · {year}</button>
          <button className="primary-btn" onClick={download} data-testid="report-download-button"><FileDown size={16} />Rapor indir · {MONTH_NAMES[month - 1]} {year}</button>
        </div>
      </div>
      <div className="report-toolbar">
        <span className="eyebrow" style={{ margin: 0 }}>YIL</span>
        <select data-testid="report-year-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {[thisYear + 1, thisYear, thisYear - 1, thisYear - 2, thisYear - 3].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <span className="eyebrow" style={{ margin: "0 0 0 14px" }}>AY</span>
        <select data-testid="report-month-select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
          {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <span className="eyebrow" style={{ margin: "0 0 0 14px" }}>PDF GRAFİĞİ</span>
        <select data-testid="report-chart-select" value={chart} onChange={(e) => setChart(e.target.value)}>
          <option value="donut">Halka grafik</option>
          <option value="bars">Çubuk grafik</option>
        </select>
      </div>
      {!data ? <p className="empty" data-testid="report-loading">Rapor hazırlanıyor…</p> : (
        <>
          <Totals testid="report-year-totals" items={[[`${year} toplam gelir`, data.totals.income, ArrowUpRight, "income-text"], [`${year} toplam gider`, data.totals.expense, ArrowDownRight, "expense-text"], [`${year} net kâr`, data.totals.net, Scale, data.totals.net < 0 ? "negative" : ""]]} />
          <div className="fin-grid"><MonthChart months={data.months} onSelect={setMonth} /><MonthTable months={data.months} totals={data.totals} month={month} onSelect={setMonth} /></div>
          {detail ? <MonthDetail year={year} month={month} d={detail} /> : <p className="empty" data-testid="report-detail-loading">Ay detayı yükleniyor…</p>}
        </>
      )}
    </>
  );
}
